import { useCallback, useEffect, useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { ErgebnisBlockA, Taste, euro, fehlerText } from '@/components/checkup/ErgebnisSeite';

/* Abschluss-Check-up nach 3 Monaten, Schritt 3 „Dein Weg ab jetzt“ (Coach-Cockpit W1.6b, backend/checkup_ergebnis.py).
   Block A Ergebnis über 3 Monate · Block B Kursempfehlung nach Ziel (NUR Kurse mit freien Plätzen, 1–2 wählen, Pflichtsatz) ·
   Block C Übergang: passende Mitgliedschaft = kein neuer Vertrag (Kurse werden in der App vorgemerkt, PT endet, offene
   Einheiten bleiben); sonst Tarif aus den bestehenden Tarifen (Preise vom Server) → Verkaufsrechner mit Unterschrift-Station;
   Ernährung weiter nur auf Nachfrage (Standard: endet). Der Server prüft alles noch einmal. */

const EINHEITEN = [[1, '1× pro Woche'], [2, '2× pro Woche'], [3, '3× oder mehr']];

export default function WegSeite({ client, ak, crm = false, onWeiter }) {
  const [r, setR] = useState(null);
  const [fehler, setFehler] = useState('');
  const [arbeitet, setArbeitet] = useState(false);
  const [kurse, setKurse] = useState([]);
  const [satz, setSatz] = useState('');
  const [einheiten, setEinheiten] = useState(1);
  const [uebergang, setUebergang] = useState(null);
  const [tarif, setTarif] = useState(null);
  const [ernaehrung, setErnaehrung] = useState(false);

  const uebernehmen = useCallback((d) => {
    setR(d);
    const w = d.weg || {};
    const g = w.gespeichert;
    setEinheiten(Math.min(w.einheiten_pro_woche || 1, 3));
    if (g) {
      setKurse((g.kurse || []).map((k) => k.kurs_id));
      setSatz(g.coach_satz || '');
      setUebergang(g.uebergang?.art || null);
      setTarif(g.uebergang?.tarif_id || null);
      setErnaehrung(!!g.ernaehrung_weiter);
    } else {
      setUebergang(w.mitgliedschaft?.stand === 'keine' ? 'tarif' : 'bestehend');
      setTarif(w.empfohlener_tarif || null);
    }
  }, []);
  const laden = useCallback(async () => {
    try { uebernehmen((await client.ergebnis()).data); setFehler(''); } catch (e) { setFehler(fehlerText(e)); }
  }, [client, uebernehmen]);
  useEffect(() => { laden(); }, [laden]);
  if (!r) return <div className="py-10 flex justify-center">{fehler ? <p className="text-red-600">{fehler}</p> : <Loader2 className="w-8 h-8 animate-spin" />}</div>;

  const w = r.weg || {};
  const m = w.mitgliedschaft || {};
  // Anzeige: welcher Tarif passt zur gewählten Zahl der Einheiten (die Kontingente kommen vom Server, entschieden wird dort)
  const passtTarif = (t) => t.kontingent_woche == null || t.kontingent_woche >= einheiten;
  const empfohlen = (w.tarife || []).find(passtTarif)?.tarif_id;
  const passend = m.stand !== 'keine' && (m.je_woche == null || m.je_woche >= einheiten);
  const gespeichert = w.gespeichert;
  const waehle = (id) => setKurse((x) => (x.includes(id) ? x.filter((y) => y !== id) : x.length >= (w.kurse_max || 2) ? x : [...x, id]));
  const art = passend ? 'bestehend' : uebergang;
  const bereit = satz.trim() && (!w.kurse?.length || kurse.length >= 1) && art && (art !== 'tarif' || tarif);
  const speichern = async () => {
    setArbeitet(true); setFehler('');
    try {
      uebernehmen((await client.weg({ kurs_ids: kurse, coach_satz: satz, uebergang: art, tarif_id: art === 'tarif' ? tarif : null,
        ernaehrung_weiter: ernaehrung, einheiten_pro_woche: einheiten })).data);
    } catch (e) { setFehler(fehlerText(e)); } finally { setArbeitet(false); }
  };
  const ern = w.ernaehrung || {};
  return (
    <section className="space-y-6" data-testid="schritt-weg">
      <h2 className="text-2xl font-bold">{r.titel}</h2>
      <ErgebnisBlockA e={r.ergebnis} client={client} />
      {/* TestimonialFrage hier erneut einbinden, falls bisher abgelehnt oder nur Zahlen (Paket w1-testimonial) — siehe Stepper */}

      <section className="space-y-4 rounded-2xl border border-border p-4 sm:p-5" data-testid="weg-block-b">
        <h3 className="text-xl font-bold">{w.titel}</h3>
        <p className="text-base text-muted-foreground">Ab jetzt geht es mit Kursen weiter, nicht mehr mit PT. Passend zum Ziel: {(w.ziele || []).map((z) => z.label).join(' · ')}</p>
        <div className="space-y-2">
          <p className="text-base font-semibold">Einheiten pro Woche</p>
          <div className="flex flex-wrap gap-2">{EINHEITEN.map(([n, l]) => (
            <button key={n} type="button" aria-pressed={einheiten === n} onClick={() => setEinheiten(n)}
              className={`min-h-[48px] px-4 rounded-xl border text-base ${einheiten === n ? `${ak?.className || ''} font-semibold` : 'border-border'}`}
              style={einheiten === n ? ak?.style : undefined}>{l}</button>
          ))}</div>
        </div>
        <div className="space-y-2">
          <p className="text-base font-semibold">Kurse mit freien Plätzen (Woche ab {String(w.woche || '').slice(8, 10)}.{String(w.woche || '').slice(5, 7)}.) – 1 bis {w.kurse_max || 2} wählen</p>
          {!(w.kurse || []).length && <p className="text-base text-muted-foreground">Gerade hat kein passender Kurs einen freien Platz. Der Satz reicht – die Kurse kommen im Gespräch.</p>}
          <div className="grid gap-2 sm:grid-cols-2">
            {(w.kurse || []).map((k) => {
              const an = kurse.includes(k.kurs_id);
              return (
                <button key={k.kurs_id} type="button" aria-pressed={an} onClick={() => waehle(k.kurs_id)} data-testid={`weg-kurs-${k.kurs_id}`}
                  className={`text-left rounded-2xl border-2 p-3 min-h-[56px] ${an ? ak?.rahmen || '' : 'border-border'}`} style={an ? ak?.rahmenStyle : undefined}>
                  <span className="block text-base font-semibold">{an ? '✓ ' : ''}{k.name}</span>
                  <span className="block text-sm text-muted-foreground">{k.tag_lang} {k.start}–{k.ende} · {k.frei_erwartet} {k.frei_erwartet === 1 ? 'Platz' : 'Plätze'} frei · {k.ziel_text}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-base font-semibold">Ein Satz von dir *</p>
          <textarea value={satz} onChange={(e) => setSatz(e.target.value)} rows={2} maxLength={300} data-testid="weg-satz"
            placeholder="z. B. Mit S&C zweimal pro Woche hältst du deine Kraftwerte."
            className="w-full rounded-xl border border-border bg-background p-3 text-base" />
        </div>
      </section>

      <section className="space-y-4 rounded-2xl border border-border p-4 sm:p-5" data-testid="weg-block-c">
        <h3 className="text-xl font-bold">Übergang</h3>
        {passend ? (
          <p className="text-base" data-testid="weg-bestehend">
            Die Mitgliedschaft <b>{m.tarif || 'des Kunden'}</b> passt{m.je_woche == null ? ' (unbegrenzt)' : ` (${m.je_woche}× pro Woche)`} – <b>kein neuer Vertrag</b>.
            Die Kurse werden in der App vorgemerkt, PT endet. Offene PT-Einheiten bleiben gültig.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-base">{m.stand === 'upgrade'
              ? `Die Mitgliedschaft ${m.tarif || ''} erlaubt ${m.je_woche}× pro Woche – für ${einheiten}× passt ein größerer Tarif.`
              : 'Bisher nur PT, keine Mitgliedschaft: Umstieg in eine Kursmitgliedschaft.'}</p>
            <div className="grid gap-2 sm:grid-cols-3">
              {(w.tarife || []).map((t) => {
                const an = art === 'tarif' && tarif === t.tarif_id;
                const ab = (t.laufzeiten || []).reduce((x, l) => (l.monatlich != null && (x == null || l.monatlich < x) ? l.monatlich : x), null);
                return (
                  <button key={t.tarif_id} type="button" aria-pressed={an} onClick={() => { setUebergang('tarif'); setTarif(t.tarif_id); }}
                    data-testid={`weg-tarif-${t.tarif_id}`}
                    className={`text-left rounded-2xl border-2 p-3 min-h-[56px] space-y-1 ${an ? ak?.rahmen || '' : 'border-border'}`} style={an ? ak?.rahmenStyle : undefined}>
                    <span className="flex items-center justify-between gap-2"><span className="text-base font-semibold">{t.name}</span>
                      {t.tarif_id === empfohlen && <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ak?.className || ''}`} style={ak?.style}>Empfohlen</span>}</span>
                    <span className="block text-sm text-muted-foreground">{t.einheiten}</span>
                    {ab != null && <span className="block text-sm tabular-nums">ab {euro(ab)} im Monat</span>}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              {m.stand === 'upgrade' && (
                <button type="button" aria-pressed={art === 'bestehend'} onClick={() => setUebergang('bestehend')}
                  className={`min-h-[48px] px-4 rounded-xl border text-base ${art === 'bestehend' ? 'font-semibold border-foreground' : 'border-border'}`}>Bei der bestehenden bleiben</button>
              )}
              <button type="button" aria-pressed={art === 'keiner'} onClick={() => setUebergang('keiner')}
                className={`min-h-[48px] px-4 rounded-xl border text-base ${art === 'keiner' ? 'font-semibold border-foreground' : 'border-border'}`}>Kein Übergang</button>
            </div>
            {art === 'tarif' && <p className="text-sm text-muted-foreground">Der Vertrag entsteht im Verkaufsrechner mit Unterschrift-Station – nach dem Abschluss bekommt der Vertrieb eine Aufgabe mit dem Link.</p>}
          </div>
        )}
        <label className="flex items-start gap-3 text-base min-h-[44px] rounded-xl border border-border p-3" data-testid="weg-ernaehrung">
          <input type="checkbox" checked={ernaehrung} onChange={(e) => setErnaehrung(e.target.checked)} className="mt-1 h-6 w-6" />
          <span>Ernährungscoaching weiter{ern.preis_monat != null ? ` (${euro(ern.preis_monat)} im Monat)` : ''}
            <span className="block text-sm text-muted-foreground">Standard: endet mit der Ergänzung{ern.gratis_bis ? ` (inklusive bis ${String(ern.gratis_bis).slice(8, 10)}.${String(ern.gratis_bis).slice(5, 7)}.)` : ''}. Bitte aktiv fragen – gebucht wird im Abschluss.</span></span>
        </label>
      </section>

      {fehler && <p className="rounded-xl bg-red-50 text-red-700 p-3 text-base" role="alert">{fehler}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Taste ak={ak} rot={!gespeichert} disabled={arbeitet || !bereit} onClick={speichern} data-testid="weg-speichern">
          {arbeitet ? <Loader2 className="inline w-5 h-5 animate-spin" /> : gespeichert ? 'Änderung speichern' : 'Weg speichern'}</Taste>
        {gespeichert && <span className="flex items-center gap-1 text-sm font-semibold"><Check className="w-4 h-4" /> gespeichert</span>}
        {gespeichert && <Taste ak={ak} rot onClick={() => onWeiter(gespeichert.ernaehrung_weiter ? ['ernaehrung'] : [])} data-testid="weiter-abschluss">Weiter zum Abschluss</Taste>}
        {crm && w.verkauf_link && <a href={w.verkauf_link} target="_blank" rel="noreferrer" className="min-h-[48px] px-5 rounded-xl border border-border text-base font-semibold inline-flex items-center"
          data-testid="weg-verkaufsrechner">Zum Verkaufsrechner</a>}
      </div>
    </section>
  );
}
